import type { ForwardedRef, RefObject } from 'react';
import { useMemo } from 'react';

export type UseForwardRefProps<T> = {
    forwardedRef?: ForwardedRef<T>;
};

function updateRef<T>(ref: ForwardedRef<T> | undefined, value: T | null): void {
    if (ref) {
        if ('current' in ref) {
            ref.current = value;
        } else {
            ref(value);
        }
    }
}

function getValue<T>(
    refs: Array<ForwardedRef<T> | undefined>,
    defValue: T | null = null,
): T | null {
    for (let ref of refs) {
        if (ref && 'current' in ref) {
            return ref.current;
        }
    }
    return defValue;
}

/**
 * Создает новый объект RefObject, синхронизированный с переданными рефами
 * @param props параметр
 * - forwardedRef - переданный реф. Если это RefObject, то значение current берется из него
 * @returns новый реф
 */
export const useForwardRef = <T>({ forwardedRef }: UseForwardRefProps<T>): RefObject<T> => {
    return useMemo(() => {
        let current = getValue([forwardedRef]);

        return {
            get current(): T | null {
                current = getValue([forwardedRef], current);

                return current;
            },
            set current(value: T | null) {
                current = value;
                updateRef(forwardedRef, value);
            },
        };
    }, [forwardedRef]);
};
